import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useState, type MouseEvent, type ReactNode } from "react";

/**
 * Brand-tinted action bar that appears whenever rows are selected.
 * Generic — pass any React nodes as actions. Pairs with `useBulkSelection`
 * for table-level workflows (delete, bulk transition, export, …).
 *
 * Renders nothing when `count === 0`, so callers can mount it
 * unconditionally.
 */
export function BulkActionBar({
  count,
  onClear,
  children,
}: {
  count: number;
  onClear: () => void;
  /** Action buttons / menus. Stacked horizontally on the right. */
  children: ReactNode;
}): JSX.Element | null {
  if (count === 0) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "primary.main",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        px: 2,
        py: 1.25,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: "primary.main" }}>
        {count} selected
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {children}
        <Button
          type="button"
          onClick={onClear}
          variant="text"
          color="inherit"
          size="small"
          sx={{ minWidth: 0, height: 32, px: 1, fontSize: 12, color: "text.disabled", "&:hover": { color: "text.primary" } }}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
}

/** Plain button — neutral or destructive variant. */
export function BulkButton({
  onClick,
  disabled,
  variant = "neutral",
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  variant?: "neutral" | "destructive";
  children: ReactNode;
}): JSX.Element {
  return (
    <Button
      type="button"
      onClick={onClick}
      {...(disabled !== undefined ? { disabled } : {})}
      variant="outlined"
      color={variant === "destructive" ? "error" : "inherit"}
      size="small"
      sx={{
        height: 32,
        px: 1.5,
        fontSize: 12,
        fontWeight: 500,
        ...(variant === "destructive"
          ? { bgcolor: (t) => alpha(t.palette.error.main, 0.05) }
          : { borderColor: "divider", bgcolor: "background.paper", color: "text.primary", "&:hover": { bgcolor: "action.hover" } }),
      }}
    >
      {children}
    </Button>
  );
}

/**
 * Dropdown-style action with a list of options. Closes on click-outside
 * and on item-select (MUI `Menu` provides both + Escape + focus management).
 */
export function BulkMenuButton({
  label,
  items,
  disabled,
}: {
  label: string;
  items: { id: string | number; label: string; onSelect: () => void; disabled?: boolean }[];
  disabled?: boolean;
}): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        disabled={disabled || items.length === 0}
        aria-expanded={open}
        aria-haspopup="menu"
        variant="outlined"
        color="inherit"
        size="small"
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
        sx={{
          height: 32,
          px: 1.5,
          fontSize: 12,
          fontWeight: 500,
          borderColor: "divider",
          bgcolor: "background.paper",
          color: "text.primary",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 200, mt: 0.5 } } }}
      >
        {items.map((it) => (
          <MenuItem
            key={it.id}
            {...(it.disabled !== undefined ? { disabled: it.disabled } : {})}
            onClick={() => {
              setAnchorEl(null);
              it.onSelect();
            }}
            sx={{ fontSize: 12 }}
          >
            {it.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
